import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import scrollIntoView from 'scroll-into-view-if-needed';
import './choicetree.scss';
import ChoiceTreeItem from './item';
import { size } from './utils/immutable';
import * as defaultSelectors from './utils/selectors';

class ChoiceTree extends PureComponent {
  static demoProps = [
    {
      options: [
        {
          key: 11010,
          label: 'ChoiceTree_TREE option1',
          value: {},
          children: [
            {
              key: 111010,
              label: 'ChoiceTree_child1',
              children: [{ key: 1111010, label: 'ChoiceTree_child11' }],
            },
            { key: 121010, label: 'ChoiceTree_child2', children: [] },
          ],
        },
        {
          key: 21010,
          label: 'ChoiceTree_TREE option2',
          value: {},
          children: [
            {
              key: 211010,
              label: 'ChoiceTree_child3',
              children: [
                { key: 2111010, label: 'ChoiceTree_child31' },
                { key: 2121010, label: 'ChoiceTree_child32' },
              ],
            },
            { key: 221010, label: 'ChoiceTree_child2', children: [] },
          ],
        },
        { key: 31010, label: 'ChoiceTree_TREE option3', value: {} },
        { key: 41010, label: 'ChoiceTree_TREE option4', value: {} },
      ],
      focusPath: [11010, 21010],
    },
  ];

  static demoHandlers = {
    onSelect: {
      prop: 'selectedPath',
      handler: (path) => path,
    },
  };

  static defaultProps = {
    className: null,
    selectedPath: [],
    focusPath: [],
    extraOptions: {},
    multiple: false,
    noToggleIcon: false,
    isStatic: false,
    scrollable: true,
    selectors: defaultSelectors,
    flatten: false,
    matchText: null,
    scrollIntoView: false,
  };

  optionRef = createRef();

  componentDidMount() {
    const { focusPath, options } = this.props;
    if (this.props.scrollIntoView) {
      this.scrollIntoView();
    }

    const container = this.optionRef.current;
    if (
      container &&
      Array.isArray(focusPath) &&
      focusPath.length > 0 &&
      focusPath[0] === (size(options) || 0) - 1
    ) {
      container.scrollTop = container.offsetHeight;
    }
  }

  scrollToItem = (element) => {
    const root = this.optionRef.current;
    if (!root) {
      return;
    }
    const deltaKeyDown =
      element.offsetTop +
      element.offsetHeight -
      root.scrollTop -
      root.offsetHeight;
    const deltaKeyUp = root.scrollTop - element.offsetTop;

    if (deltaKeyDown > 0) {
      root.scrollTop += deltaKeyDown;
    } else if (deltaKeyUp > 0) {
      root.scrollTop -= deltaKeyUp;
    }
  };

  scrollIntoView() {
    const container = this.optionRef.current;
    scrollIntoView(container, { scrollMode: 'if-needed' });
  }

  render() {
    const {
      options,
      className,
      focusPath,
      extraOptions,
      selectedPath,
      id,
      multiple,
      noToggleIcon,
      isStatic,
      scrollable,
      selectors,
      onSelect,
      flatten,
      matchText,
      renderItem,
    } = this.props;

    const listClasses = {
      ChoiceTree: true,
      'ChoiceTree--static': isStatic,
      'ChoiceTree--scrollable': scrollable,
    };

    return (
      <div
        className={classNames(className, listClasses)}
        ref={this.optionRef}
        id={id}
      >
        {options.map((item, i) => {
          const itemId = selectors.selectId(item, i);
          const key = `${id}-${itemId}`;
          return (
            <ChoiceTreeItem
              key={key}
              id={key}
              item={item}
              focusPath={focusPath}
              extraOptions={extraOptions}
              onSelect={onSelect}
              selectedPath={selectedPath}
              scrollToItem={this.scrollToItem}
              multiple={multiple}
              noToggleIcon={noToggleIcon}
              selectors={selectors}
              path={[itemId].join()}
              flatten={flatten}
              matchText={matchText}
              renderItem={renderItem}
            />
          );
        })}
      </div>
    );
  }
}

ChoiceTree.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  options: PropTypes.oneOfType([PropTypes.array, ImmutablePropTypes.list])
    .isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedPath: PropTypes.array,
  focusPath: PropTypes.array,
  extraOptions: PropTypes.object,
  multiple: PropTypes.bool,
  noToggleIcon: PropTypes.bool,
  isStatic: PropTypes.bool,
  scrollable: PropTypes.bool,
  selectors: PropTypes.shape({
    selectId: PropTypes.func.isRequired,
    selectLabel: PropTypes.func.isRequired,
  }).isRequired,
  flatten: PropTypes.bool,
  matchText: PropTypes.string,
  scrollIntoView: PropTypes.bool,
  renderItem: PropTypes.func,
};

export default ChoiceTree;
